import {IAddon, IAddonFeature, IAddonRefershedStatus} from "../../../../models/Addon";
import {makeAutoObservable, runInAction} from "mobx";
import {rootContainer, TYPE} from "../../../../services/container/root";
import AddonsService from "../../../../services/api/addons-service";
import {BadgeType} from "../../../../components/badge/badge";
import SubscriptionPlansStore from "../../../subscription-plans/stores/subscription-plans-store";
import subscriptionPlansStore from "../../../subscription-plans/stores/subscription-plans-store";

export enum AddonStatuses {
    Active = 'ADDON_STATUS_INCLUDED_AS_ADDON',
    Deactivated = 'ADDON_STATUS_NOT_INCLUDED',
    InPlan = 'ADDON_STATUS_INCLUDED_AS_PLAN_FEATURES',
    Partially = 'ADDON_STATUS_PARTIALLY_AS_PLAN_FEATURES',
    PartiallyAsAddonFeatures = 'ADDON_STATUS_PARTIALLY_AS_ADDON_FEATURES',
    IncludedAsDeprecated = 'ADDON_STATUS_INCLUDED_AS_DEPRECATED_ADDON',
}

interface IAddonsStatusUI {
    [key: string]: { badgeClass: BadgeType, status: string };
}

export const AddonsStatusesLabelColorsMap: IAddonsStatusUI = {
    'ADDON_STATUS_INCLUDED_AS_ADDON': {
        badgeClass: BadgeType.SUCCESS,
        status: 'Activated'
    },
    'ADDON_STATUS_INCLUDED_AS_PLAN_FEATURES': {
        badgeClass: BadgeType.INFO,
        status: 'Included in plan'
    },
    'ADDON_STATUS_PARTIALLY_AS_PLAN_FEATURES': {
        badgeClass: BadgeType.INFO,
        status: 'Partially included in plan'
    },
    'ADDON_STATUS_PARTIALLY_AS_ADDON_FEATURES': {
        badgeClass: BadgeType.INFO,
        status: 'Partially included as addon features'
    },

    /** This status should not appear in list at all but this added for consistency */
    'ADDON_STATUS_INCLUDED_AS_DEPRECATED_ADDON': {
        badgeClass: BadgeType.INFO,
        status: 'Included as deprecated addon'
    },
}

export interface IAddonChanges extends Omit<Partial<IAddon>, 'features'> {
    features?: IAddonFeatureChanges[]
}

export class AddonStore {
    private readonly _model: IAddon;
    private _changes: IAddonChanges = {};
    private _refreshedAddon: IAddonRefershedStatus = {} as IAddonRefershedStatus;
    private _features: AddonFeatureStore[] = [];

    constructor(addon: IAddon) {
        this._model = addon;
        this._model._isEnabled = this._model.addon_status === AddonStatuses.Active;
        this._features = addon.features.map((feature) => {
            return new AddonFeatureStore(feature);
        });
        makeAutoObservable(this);
    }

    public get id() { return this._model?.id ?? 0; }
    public get kbArticleLink() { return this._model.kb_article_link; }
    public get displayName() { return this._model.display_name; }
    public get description() { return this._model.description; }
    public get paymentType() { return this._model.payment_type; }
    public get deprecated() { return this._model.deprecated; }
    public get featurePrice(): number { return this._features[0].featurePrice; }
    public get featureQuota(): number { return this._features[0].quota; }
    public get quotaUnit(): number { return this._features[0].quotaUnit || 1; }
    public get addonStatus() { return this._model.addon_status; }
    public get addonFeaturePluralName(){ return this._features[0].pluralName; }

    public get currentUserQuota() {
        return SubscriptionPlansStore.userPlan?.getQuotaFromAddon(this.getQuotaBasedFeatureId()) || 0;
    }

    public get originalPrice() {
        return this._model.addon_price;
    }

    public set refreshedAddon(addon: IAddonRefershedStatus) {
        this._refreshedAddon = addon;
    }

    public hasChanges(): boolean {
        return Object.keys(this._changes).length > 0;
    }

    public getUpdatedModel(): IAddonChanges {
        return { ...this._model, ...this._changes };
    }

    public getQuotaBasedFeatureId() {
        /** addon might have few features - for now we only treat it as one */
        return this._features[0].featureId;
    }

    public isQuotaBased(): boolean {
        return this._features.filter((feature) => {
            return feature.isQuotaBased;
        }).length > 0;
    }

    public get addonPrice() {
        if (this.isQuotaBased()) {
            const quotaBasedPrice = this._features.map((feature) => {
                return feature.getQuotaBasedPrice();
            }).reduce((addonPrice: number, currentFeaturePrice: number) => {
                return addonPrice + currentFeaturePrice;
            });
            return (quotaBasedPrice % 1 === 0) ? Number(quotaBasedPrice.toFixed()) : quotaBasedPrice;
        }
        return this._model.addon_price;
    }

    public setFeatureQuota(quota: number) {
        this._features[0].quota = quota;
        if (this._features[0].hasChanges()) {
            const isAddonRemoved = !(-quota >= this.currentUserQuota);
            this.setIsEnabled(isAddonRemoved);
            this._changes.features = [this._features[0].getUpdatedModel()];
        } else {
            this._changes = {};
        }
    }

    /** is shown on list */
    public get isAvailable(): boolean {
        const status = this._refreshedAddon?.addon_status ?? this._model.addon_status;
        return [AddonStatuses.Active, AddonStatuses.Deactivated].includes(status);
    }

    public isEnabled(): boolean {
        return this._changes._isEnabled ?? this._model._isEnabled;
    }

    public setIsEnabled(v: boolean): void {
        this._changes._isEnabled = v;
        if (this._model._isEnabled === this._changes._isEnabled) {
            delete this._changes._isEnabled;
        }
    }

}

interface IAddonFeatureChanges extends Partial<IAddonFeature> {
    quota?: number
}

export class AddonFeatureStore {
    private readonly _model: IAddonFeature;
    private _changes: IAddonFeatureChanges = {};

    constructor(feature: IAddonFeature) {
        this._model = this.alignModelTypes(feature);
        makeAutoObservable(this);
    }

    public get featureId() { return this._model.feature_id; }
    public get isQuotaBased() { return this._model.is_quota_based; }
    public get quotaUnit() { return this._model.quota_unit || 1; }
    public get pluralName(): string | undefined {return  this._model.plural_name; }

    public get featurePrice() {
        return this._model.feature_price % 1 === 0 ?
            Number(this._model.feature_price.toFixed()) :
            this._model.feature_price;
    }

    private alignModelTypes(data: IAddonFeature) {
        const toNum = (v: undefined | any) => v !== undefined ? Number(v) : v;
        const toBool = (v: undefined | any) => Boolean(v);

        return {
            ...data,
            feature_id: toNum(data.feature_id),
            feature_price: toNum(data.feature_price),
            is_quota_based: toBool(data.is_quota_based),
            quota_unit: toNum(data.quota_unit),
        }
    }

    public hasChanges(): boolean {
        return Object.keys(this._changes).length > 0;
    }

    public getUpdatedModel(): IAddonFeatureChanges {
        return { ...this._model, ...this._changes };
    }

    public getQuotaBasedPrice() {
        return (this.quota / this.quotaUnit) * this.featurePrice;
    }

    public get quota() {
        return this._changes.quota || 0;
    }

    public set quota(quota: number) {
        if (quota === 0) {
            this._changes = {};
            return;
        }
        this._changes.quota = quota;
    }
}


class AddonsStoreList {
    private _isLoading = false;
    private _addonsList: AddonStore[] = [];
    private _approve_billing_link: string = '';
    private _is_generating_link: boolean = false;
    private _is_refreshing_addons: boolean = false;
    private _userAddonsStoresList: AddonStore[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    public get isRefreshingAddons() { return this._is_refreshing_addons; }
    public get isLoading() { return this._isLoading; }
    public get isGeneratingBillingLink() { return this._is_generating_link; }
    public get userAddonsStores() { return this._userAddonsStoresList; }

    get addonsList(): AddonStore[] {
        return this._addonsList;
    }

    private sortAddonsByQuotaBased(){
        return this._addonsList.slice().sort((a, b) => {
            if(a.isQuotaBased() && b.isQuotaBased()){
                return 0;
            }
            if(!a.isQuotaBased() && b.isQuotaBased()){
                return 1;
            }
            return -1
        });
    }

    public hasChanges(): boolean {
        return this._addonsList.some(addonStore => addonStore.hasChanges());
    }

    private _getChangedAddons(): AddonStore[] {
        return this._addonsList.filter(addon => addon.hasChanges());
    }

    private get addonsService(): AddonsService {
        return rootContainer.get<AddonsService>(TYPE.AddonsService);
    }

    async load() {
        this._isLoading = true;
        const [addonsResponse] = await Promise.all([this.addonsService.getAddons(), subscriptionPlansStore.load()]);
        runInAction(() => {
            if (subscriptionPlansStore.userPlan) {
                /** REFACTOR: remove _userAddonsStoresList from this store and enhance PlanStore with addons functionality */
                this._userAddonsStoresList = subscriptionPlansStore.userPlan.userAddons;
                this._addonsList = addonsResponse.map(addon => new AddonStore(addon));
                this._addonsList = this.sortAddonsByQuotaBased();
                this._isLoading = false;
            }
        });
    }

    public async refreshAddonsStatuses(addon: AddonStore, isAddonEnabled: boolean) {
        addon.setIsEnabled(isAddonEnabled);
        this._is_refreshing_addons = true;
        const changedAddons = this._getChangedAddons().map((a) => a.getUpdatedModel());
        const refreshedAddonsList = await this.addonsService.getRefreshedAddonsList(changedAddons);
        const addonList = this._addonsList;
        runInAction(() => {
            if (refreshedAddonsList.length !== addonList.length) {
                throw new Error('Addons where updated - please refresh');
            }
            refreshedAddonsList.forEach(function (addonStatus) {
                const addonStore = addonList.find(addonStore => addonStore.id === addonStatus.id);
                if (!addonStore) {
                    throw new Error('Addon to refresh not found');
                }
                addonStore.refreshedAddon = addonStatus;
            });
            this._is_refreshing_addons = false;

        });
    }

    public getActiveUnchangedAddons(): AddonStore[] {
        const on_off_unchanged_addons_list = this._addonsList.filter(addon => !addon.hasChanges() && addon.isEnabled());
        this._userAddonsStoresList.forEach(userAddon => {
            if (userAddon.isQuotaBased()
                && !on_off_unchanged_addons_list.find(unchangedAddon => unchangedAddon.id === userAddon.id)
                && !this.getAddonsToAdd().find(addonToAdd => addonToAdd.id === userAddon.id)
                && !this.getAddonsToRemove().find(addonToAdd => addonToAdd.id === userAddon.id)
            ) {
                on_off_unchanged_addons_list.push(userAddon);
            }
        });
        return on_off_unchanged_addons_list;
    }

    public async generateBillingLink() {
        if (this._is_generating_link) {
            return false;
        }
        const addons_to_add = this.getAddonsToAdd().concat(this.getActiveUnchangedAddons());
        const addons_to_add_models = addons_to_add.map(function (addon) {
            return addon.getUpdatedModel();
        })
        this._is_generating_link = true;

        try {
            const generateBillingLinkRequest = await this.addonsService.generateBillingLink(addons_to_add_models);
            runInAction(() => {
                    this._approve_billing_link = generateBillingLinkRequest;
                    window.open(this._approve_billing_link, '_blank');
                    this._is_generating_link = false;
                }
            );
        } catch {
            this._is_generating_link = false;
        }
    }

    public getAddonsToAdd(): AddonStore[] {
        return this._getChangedAddons().filter((addon) => {
            return addon.isEnabled();
        });
    }

    public getAddonsToRemove(): AddonStore[] {
        return this._getChangedAddons().filter((addon) => {
            return !addon.isEnabled();
        });
    }

}

export default new AddonsStoreList();